/**
 * ReviewPhotoLightbox — full-screen dark overlay for photo review.
 *
 * Supports left/right navigation via arrow buttons and keyboard (←/→).
 * Escape closes. Backdrop click closes. Images fade-in on change so
 * navigating between three photos doesn't strobe.
 */

import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { reviewPhotoUrl } from "../../lib/reviewsApi";

export default function ReviewPhotoLightbox({
    photos = [],
    index = 0,
    onClose,
    onIndex,
}) {
    const total = photos.length;

    const goPrev = useCallback(() => {
        if (total <= 1) return;
        onIndex((index - 1 + total) % total);
    }, [index, total, onIndex]);

    const goNext = useCallback(() => {
        if (total <= 1) return;
        onIndex((index + 1) % total);
    }, [index, total, onIndex]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            else if (e.key === "ArrowLeft") goPrev();
            else if (e.key === "ArrowRight") goNext();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [goPrev, goNext, onClose]);

    if (!photos[index]) return null;
    const photo = photos[index];

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Review photos"
            data-testid="review-lightbox"
            className="fixed inset-0 z-[80] bg-[#0B0B0C]/95 backdrop-blur-md flex items-center justify-center px-4 py-8"
            onClick={onClose}
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Close photo viewer"
                className="absolute top-6 right-6 text-[#F3EFE7] hover:text-[#C9A86A] transition-colors"
                data-testid="review-lightbox-close"
            >
                <X size={28} strokeWidth={1.4} />
            </button>

            {total > 1 && (
                <>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            goPrev();
                        }}
                        aria-label="Previous photo"
                        className="absolute left-4 md:left-10 text-[#F3EFE7] hover:text-[#C9A86A] transition-colors p-2"
                        data-testid="review-lightbox-prev"
                    >
                        <ChevronLeft size={40} strokeWidth={1.2} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            goNext();
                        }}
                        aria-label="Next photo"
                        className="absolute right-4 md:right-10 text-[#F3EFE7] hover:text-[#C9A86A] transition-colors p-2"
                        data-testid="review-lightbox-next"
                    >
                        <ChevronRight size={40} strokeWidth={1.2} />
                    </button>
                </>
            )}

            <img
                src={reviewPhotoUrl(photo.url)}
                alt={photo.original_filename || "Review photo"}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[90vh] max-w-full object-contain border border-[rgba(201,168,106,0.2)]"
                data-testid="review-lightbox-image"
            />

            {total > 1 && (
                <div
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10.5px] tracking-[0.28em] uppercase text-[#9A958A]"
                    data-testid="review-lightbox-counter"
                >
                    {index + 1} / {total}
                </div>
            )}
        </div>
    );
}
